import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Heart, ShoppingBag, Minus, Plus, Truck, RotateCcw, Shield } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../lib/api";
import { useCart } from "../contexts/CartContext";

export default function ProductDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeImg, setActiveImg] = useState(0);
    const [size, setSize] = useState(null);
    const [color, setColor] = useState(null);
    const [qty, setQty] = useState(1);
    const { addToCart, toggleWishlist, inWishlist } = useCart();

    useEffect(() => {
        setLoading(true);
        api.get(`/products/${id}`)
            .then(({ data }) => {
                setProduct(data);
                setSize(data.sizes?.[0] || null);
                setColor(data.colors?.[0] || null);
                setActiveImg(0);
            })
            .catch(() => navigate("/shop"))
            .finally(() => setLoading(false));
    }, [id, navigate]);

    if (loading || !product) {
        return (
            <div className="mx-auto grid min-h-[60vh] max-w-[1480px] place-items-center px-6">
                <span className="font-display text-3xl text-gold">Loading…</span>
            </div>
        );
    }

    const add = async () => {
        if (product.sizes?.length && !size) return toast.error("Choose a size");
        const res = await addToCart(product.id, qty, size, color);
        if (!res.ok) toast.error(res.error || "Sign in to add to bag");
        else toast.success("Added to bag");
    };

    const liked = inWishlist(product.id);
    const handleWish = async () => {
        const r = await toggleWishlist(product.id);
        if (!r.ok) toast.error(r.error || "Sign in first");
    };

    return (
        <div className="bg-obsidian" data-testid="product-detail-page">
            <div className="mx-auto grid max-w-[1480px] grid-cols-1 gap-12 px-6 py-12 lg:grid-cols-2 lg:gap-16 lg:px-12 lg:py-20">
                {/* Gallery */}
                <div>
                    <div className="aspect-[4/5] overflow-hidden bg-surface">
                        <img src={product.images?.[activeImg]} alt={product.name} className="h-full w-full object-cover" />
                    </div>
                    {product.images?.length > 1 && (
                        <div className="mt-4 flex gap-3">
                            {product.images.map((src, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveImg(i)}
                                    className={`h-20 w-16 overflow-hidden border ${
                                        activeImg === i ? "border-gold" : "border-border-dim"
                                    }`}
                                >
                                    <img src={src} alt="" className="h-full w-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Details */}
                <div className="lg:sticky lg:top-28 lg:self-start">
                    <p className="text-[11px] uppercase tracking-[0.4em] text-gold">
                        {product.category === "clothing"
                            ? "Atelier"
                            : product.category === "home"
                              ? "Maison"
                              : "Studio"}
                    </p>
                    <h1 className="mt-3 font-display text-3xl text-white sm:text-5xl">{product.name}</h1>
                    <div className="mt-4 flex items-baseline gap-3">
                        <span data-testid="product-price" className="text-2xl text-gold">
                            {formatPrice(product.price)}
                        </span>
                        {product.compare_at_price && product.compare_at_price > product.price && (
                            <span className="text-base text-white/40 line-through">
                                {formatPrice(product.compare_at_price)}
                            </span>
                        )}
                    </div>
                    <p className="mt-6 leading-relaxed text-white/70">{product.description}</p>

                    {product.colors?.length > 0 && (
                        <div className="mt-8">
                            <p className="text-[11px] uppercase tracking-[0.25em] text-white/50">Colour — {color}</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {product.colors.map((c) => (
                                    <button
                                        key={c}
                                        onClick={() => setColor(c)}
                                        data-testid={`color-${c}`}
                                        className={`border px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                                            color === c
                                                ? "border-gold bg-gold text-obsidian"
                                                : "border-border-dim text-white/70 hover:border-gold"
                                        }`}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {product.sizes?.length > 0 && (
                        <div className="mt-6">
                            <p className="text-[11px] uppercase tracking-[0.25em] text-white/50">Size</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {product.sizes.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setSize(s)}
                                        data-testid={`size-${s}`}
                                        className={`min-w-[3rem] border px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                                            size === s
                                                ? "border-gold bg-gold text-obsidian"
                                                : "border-border-dim text-white/70 hover:border-gold"
                                        }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-8 flex items-center gap-4">
                        <div className="inline-flex items-center border border-border-dim">
                            <button
                                onClick={() => setQty((q) => Math.max(1, q - 1))}
                                data-testid="qty-dec"
                                className="px-3 py-3 text-white/70 hover:text-gold"
                            >
                                <Minus className="h-4 w-4" />
                            </button>
                            <span data-testid="qty-value" className="min-w-[2rem] text-center">
                                {qty}
                            </span>
                            <button
                                onClick={() => setQty((q) => q + 1)}
                                data-testid="qty-inc"
                                className="px-3 py-3 text-white/70 hover:text-gold"
                            >
                                <Plus className="h-4 w-4" />
                            </button>
                        </div>
                        <button
                            onClick={add}
                            data-testid="add-to-cart-btn"
                            className="shine flex flex-1 items-center justify-center gap-3 bg-gold py-4 text-[12px] uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-gold-light"
                        >
                            <ShoppingBag className="h-4 w-4" /> Add to bag
                        </button>
                        <button
                            onClick={handleWish}
                            data-testid="pdp-wishlist"
                            className={`flex h-[52px] w-[52px] items-center justify-center border ${
                                liked ? "border-heart text-heart" : "border-border-dim text-white/70 hover:border-gold hover:text-heart"
                            }`}
                            aria-label="Wishlist"
                        >
                            <Heart className={`h-5 w-5 ${liked ? "fill-heart" : ""}`} />
                        </button>
                    </div>

                    {product.stock < 10 && product.stock > 0 && (
                        <p className="mt-4 text-xs uppercase tracking-widest text-heart">
                            Only {product.stock} left in atelier
                        </p>
                    )}
                    {product.stock === 0 && (
                        <p className="mt-4 text-xs uppercase tracking-widest text-heart">Sold out — back soon</p>
                    )}

                    <div className="mt-10 grid grid-cols-3 gap-4 border-t border-border-dim pt-8 text-xs text-white/60">
                        <div className="flex flex-col items-center gap-2 text-center">
                            <Truck className="h-5 w-5 text-gold" />
                            <span className="uppercase tracking-widest">Free over $200</span>
                        </div>
                        <div className="flex flex-col items-center gap-2 text-center">
                            <RotateCcw className="h-5 w-5 text-gold" />
                            <span className="uppercase tracking-widest">30-day returns</span>
                        </div>
                        <div className="flex flex-col items-center gap-2 text-center">
                            <Shield className="h-5 w-5 text-gold" />
                            <span className="uppercase tracking-widest">Secure pay</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
